import { useCallback, useContext } from 'react';
import useNotification, { NotificationType } from '../../../components/notification/useNotification';
import { AppContext } from '../../../taskpane/contexts/AppContext';

/**
 * Custom hook to validate metric table IDs and show warning notifications
 * when the table ID doesn't match the expected table ID.
 * Pure validator - uses AppContext to determine expected table ID.
 * Validates only for specific client versions based on AppContext.clientVersion.
 */
const useMetricTableIdValidator = () => {
    const pushNotification = useNotification();
    const appContext = useContext(AppContext);

    /**
     * Validates if the selected metric's table ID matches the expected default table ID
     * Shows a warning notification if there's a mismatch
     * Only validates for client version == 2 (version-gated feature)
     * Uses AppContext.defaultMetrics.NonBriefed.MetricTableId as the expected table ID
     * 
     * @param selectedTableId - The table ID from the selected metric
     * @param componentName - Name of the component for better error messaging (optional)
     * @returns boolean - true if table IDs match, false otherwise
     */
    const validateTableId = useCallback(
        (
            selectedTableId: string | null | undefined,
            componentName: string
        ): boolean => {
            // Skip validation if client version is not set or is version 0 or client version is not 2
            if (!appContext.clientVersion || appContext.clientVersion != 2) {
                return true;
            }

            // Get expected table ID from default metrics
            const expectedTableIds = [
                appContext.defaultMetrics?.NonBriefed?.MetricTableId,
                appContext.defaultMetrics?.Briefed?.MetricTableId
            ].filter((tableId): tableId is string => tableId!=null);


            // If either table ID is null/undefined, skip validation
            if (!selectedTableId || !expectedTableIds.length) {
                return true;
            }

            // Check if table IDs match
            if (!expectedTableIds.includes(selectedTableId)) {
                const message = `Warning: The selected metric's table is different than the default metric tables which is Supplier or Budget in ${componentName}. This may cause data inconsistencies.`;

                pushNotification(message, NotificationType.WARNING, 5000);
                return false;
            }

            return true;
        },
        [pushNotification, appContext.clientVersion, appContext.defaultMetrics]
    );

    return validateTableId;
};

export default useMetricTableIdValidator;
